import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Manager, Reference, Popper } from 'react-popper';
import Portal from '@reach/portal';
import clsx from '@strava/ui/clsx';

import styles from './styles.scss';

const Popup = ({
  children,
  position,
  className,
  offset,
  disabled,
  onShow,
  onHide,
  contents,
  isOpenOverride,
  canClick,
  contentsClassName,
  arrowClassName,
  flip,
  shouldPreventDefaultOnClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [clickedClosed, setClickedClosed] = useState(false);
  const [showTimeout, setShowTimeout] = useState(null);
  let hideTimeout;

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const handleShow = () => {
    clearTimeout(showTimeout);
    clearTimeout(hideTimeout);

    if (disabled) {
      return;
    }

    if (clickedClosed) {
      return;
    }

    setShowTimeout(
      setTimeout(() => {
        if (isOpen === false) {
          onShow();
        }
        setIsOpen(true);
      }, 100)
    );
  };

  const handleHide = () => {
    clearTimeout(showTimeout);

    setClickedClosed(false);

    hideTimeout = setTimeout(() => {
      setIsOpen(false);
      onHide();
    }, 0);
  };

  const handleClick = (e) => {
    if (!isOpen && disabled) {
      return;
    }

    // If a Popup component is rendered as the child of a component that has some other click handler
    // it may be desirable to prevent the click event from firing on the parent
    // element. The `shouldPreventDefaultOnClick` prop allows the Popup component to be clicked
    // independently of the parent element, should one exist.
    if (shouldPreventDefaultOnClick) {
      e.preventDefault();
    }

    setClickedClosed(isOpen);
    setIsOpen(!isOpen);
  };

  const onMouseOver = isOpenOverride === undefined ? handleShow : undefined;
  const onMouseLeave = isOpenOverride === undefined ? handleHide : undefined;
  const onFocus = isOpenOverride === undefined ? handleShow : undefined;
  const onBlur = isOpenOverride === undefined ? handleHide : undefined;

  return (
    <Manager>
      <Reference>
        {({ ref }) => {
          const click = canClick ? { onClick: handleClick } : null;
          const open =
            isOpenOverride === undefined
              ? {
                  onMouseOver: handleShow,
                  onMouseLeave: handleHide,
                  onFocus: handleShow,
                  onBlur: handleHide
                }
              : null;
          return React.cloneElement(children, {
            ref,
            ...click,
            ...open
          });
        }}
      </Reference>

      {(isOpen || isOpenOverride) && (
        <Portal>
          <Popper
            placement={position}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset
                }
              },
              {
                name: 'flip',
                enabled: flip
              }
            ]}
          >
            {({ style, placement: dataPlacement, ref, arrowProps }) => (
              <div
                ref={ref}
                role="tooltip"
                style={style}
                className={clsx(styles.popup, className)}
                data-placement={dataPlacement}
                onMouseOver={onMouseOver}
                onMouseLeave={onMouseLeave}
                onFocus={onFocus}
                onBlur={onBlur}
              >
                <div className={clsx(styles.contents, contentsClassName)}>
                  {contents}
                </div>
                <div
                  ref={arrowProps.ref}
                  style={arrowProps.style}
                  className={clsx(styles.arrow, arrowClassName)}
                  data-placement={dataPlacement}
                />
              </div>
            )}
          </Popper>
        </Portal>
      )}
    </Manager>
  );
};

Popup.defaultProps = {
  position: 'bottom',
  className: null,
  offset: [0, 0],
  disabled: false,
  onShow: () => {},
  onHide: () => {},
  isOpenOverride: undefined,
  canClick: true,
  contentsClassName: null,
  arrowClassName: null,
  flip: true,
  shouldPreventDefaultOnClick: false
};

Popup.propTypes = {
  children: PropTypes.node.isRequired,
  position: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  className: PropTypes.string,
  offset: PropTypes.arrayOf(PropTypes.number),
  disabled: PropTypes.bool,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  contents: PropTypes.node.isRequired,
  isOpenOverride: PropTypes.bool,
  canClick: PropTypes.bool,
  contentsClassName: PropTypes.string,
  arrowClassName: PropTypes.string,
  flip: PropTypes.bool,
  shouldPreventDefaultOnClick: PropTypes.bool
};

export default Popup;
